(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observed = new WeakSet();
  const preparedVideos = new WeakSet();
  const preparedFinders = new WeakSet();
  const preparedHeaders = new WeakSet();
  const preparedProcessingExplorers = new WeakSet();
  const preparedProcessingFilters = new WeakSet();
  const preparedProductRails = new WeakSet();
  const preparedRoastSchedules = new WeakSet();
  const preparedRangeStories = new WeakSet();
  const rangeStoryCleanups = new WeakMap();
  let observer;

  const reveal = (element) => element.classList.add('is-visible');

  const includesAny = (value, terms) => terms.some((term) => value.includes(term));

  const prepareFinder = (finder) => {
    if (preparedFinders.has(finder)) return;
    preparedFinders.add(finder);

    const form = finder.querySelector('[data-rose-finder-form]');
    const results = finder.querySelector('[data-rose-finder-results]');
    const steps = Array.from(finder.querySelectorAll('[data-rose-finder-step]'));
    const products = Array.from(finder.querySelectorAll('[data-rose-finder-product]'));
    const progress = finder.querySelector('[data-rose-finder-progress]');
    const back = finder.querySelector('[data-rose-finder-back]');
    const reset = finder.querySelector('[data-rose-finder-reset]');
    const empty = finder.querySelector('[data-rose-finder-empty]');
    const milkStep = steps.find((step) => step.querySelector('input[name="milk"]'));
    let activeSteps = steps;
    let currentStep = 0;
    let advanceTimer;

    if (!form || !results || !steps.length || !back) return;

    const refreshActiveSteps = () => {
      const brew = form.querySelector('input[name="brew"]:checked')?.value;
      const includeMilk = brew === 'espresso';
      activeSteps = steps.filter((step) => step !== milkStep || includeMilk);

      if (!includeMilk && milkStep) {
        milkStep.querySelectorAll('input').forEach((input) => {
          input.checked = false;
        });
      }
    };

    const showStep = (index, shouldFocus = true) => {
      refreshActiveSteps();
      currentStep = Math.max(0, Math.min(index, activeSteps.length - 1));
      const activeStep = activeSteps[currentStep];

      steps.forEach((step) => {
        step.hidden = step !== activeStep;
      });

      back.hidden = currentStep === 0;
      progress.textContent = `${currentStep + 1} / ${activeSteps.length}`;

      if (shouldFocus) activeStep.querySelector('input')?.focus({ preventScroll: true });
    };

    const scoreProduct = (product, answers) => {
      const brew = product.dataset.brew || '';
      const range = product.dataset.range || '';
      const flavour = `${product.dataset.style || ''}|${product.dataset.taste || ''}`;
      let score = 0;

      if (answers.brew === 'filter') score += includesAny(brew, ['filter', 'omni']) ? 6 : -6;
      if (answers.brew === 'espresso') score += includesAny(brew, ['espresso', 'omni']) ? 6 : -6;
      if (answers.brew === 'moka') score += includesAny(brew, ['moka', 'aeropress', 'espresso', 'omni']) ? 5 : -5;

      if (answers.flavour === 'comfort' && includesAny(flavour, ['chocolate', 'caramel', 'sweet', 'comfort', 'smooth', 'clean', 'gentle'])) score += 4;
      if (answers.flavour === 'fruit' && includesAny(flavour, ['fruit', 'floral', 'juicy', 'bright', 'citrus', 'berry', 'tropical'])) score += 4;
      if (answers.flavour === 'wild' && includesAny(flavour, ['wild', 'ferment', 'expressive', 'complex', 'boozy'])) score += 4;

      if (answers.milk === 'black' && includesAny(brew, ['filter', 'omni'])) score += 1;
      if (answers.milk === 'black' && includesAny(brew, ['milk']) && !includesAny(brew, ['filter', 'omni'])) score -= 2;
      if (answers.milk === 'milk' && includesAny(brew, ['milk', 'espresso', 'moka'])) score += 3;
      if (answers.milk === 'milk' && !includesAny(brew, ['milk', 'espresso', 'moka'])) score -= 3;
      if (answers.milk === 'milk' && range === 'daily') score += 1;

      if (answers.range === range) score += 7;
      if (answers.range === 'surprise' && includesAny(range, ['origins', 'obsessions'])) score += 2;

      return score;
    };

    const showResults = () => {
      const answers = Object.fromEntries(new FormData(form).entries());
      const ranked = products
        .map((product) => ({ product, score: scoreProduct(product, answers), index: Number(product.dataset.index) }))
        .sort((a, b) => b.score - a.score || a.index - b.index);

      products.forEach((product) => {
        product.hidden = true;
      });
      ranked.slice(0, 3).forEach(({ product }) => {
        product.hidden = false;
      });

      empty.hidden = ranked.length > 0;
      form.hidden = true;
      results.hidden = false;
      results.focus({ preventScroll: true });
    };

    const advanceFinder = () => {
      if (!activeSteps[currentStep]?.querySelector('input:checked')) return;
      if (currentStep < activeSteps.length - 1) {
        showStep(currentStep + 1);
      } else {
        showResults();
      }
    };

    form.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      if (event.target.name === 'brew') showStep(currentStep, false);

      window.clearTimeout(advanceTimer);
      advanceTimer = window.setTimeout(advanceFinder, 260);
    });

    back.addEventListener('click', () => showStep(currentStep - 1));

    reset?.addEventListener('click', () => {
      window.clearTimeout(advanceTimer);
      form.reset();
      products.forEach((product) => {
        product.hidden = true;
      });
      results.hidden = true;
      form.hidden = false;
      showStep(0, false);
      finder.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    });

    showStep(0, false);
  };

  const prepareHeader = (header) => {
    if (preparedHeaders.has(header)) return;
    preparedHeaders.add(header);

    const mobileMenu = header.querySelector('[data-rose-mobile-menu]');
    const mobileToggle = mobileMenu?.querySelector(':scope > summary');
    const dropdowns = Array.from(header.querySelectorAll('[data-rose-header-dropdown]'));
    const videoOverlay = header.hasAttribute('data-rose-video-overlay');

    if (videoOverlay) {
      let scrollFrame = null;
      const syncOverlay = () => {
        header.classList.toggle('is-video-top', window.scrollY <= 24);
        scrollFrame = null;
      };

      window.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(syncOverlay);
      }, { passive: true });
      syncOverlay();
    }

    mobileMenu?.addEventListener('toggle', () => {
      document.documentElement.classList.toggle('rose-menu-open', mobileMenu.open);
      mobileToggle?.setAttribute('aria-label', mobileMenu.open ? 'Close menu' : 'Open menu');
    });

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        dropdowns.forEach((other) => {
          if (other !== dropdown) other.removeAttribute('open');
        });
      });
    });

    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openDetails = event.target.closest('details[open]') || header.querySelector('details[open]');
      if (!openDetails) return;
      openDetails.removeAttribute('open');
      openDetails.querySelector(':scope > summary')?.focus();
    });

    document.addEventListener('click', (event) => {
      if (header.contains(event.target)) return;
      dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
    });

    header.addEventListener('click', (event) => {
      if (!event.target.closest('a')) return;
      dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
      mobileMenu?.removeAttribute('open');
      document.documentElement.classList.remove('rose-menu-open');
    });
  };

  const prepareProcessingExplorer = (explorer) => {
    if (preparedProcessingExplorers.has(explorer)) return;
    preparedProcessingExplorers.add(explorer);

    const input = explorer.querySelector('[data-rose-processing-input]');
    const levels = Array.from(explorer.querySelectorAll('[data-rose-processing-level]'));
    const markers = Array.from(explorer.querySelectorAll('[data-rose-processing-marker]'));
    const number = explorer.querySelector('[data-rose-processing-number]');
    const heading = explorer.querySelector('[data-rose-processing-heading]');
    const text = explorer.querySelector('[data-rose-processing-text]');
    const processingSection = explorer.closest('.rose-processing');
    const productPanels = Array.from(processingSection?.querySelectorAll('[data-rose-processing-panel]') || []);

    if (!input || !levels.length) return;

    const updateExplorer = () => {
      const level = levels[Number(input.value) - 1];
      if (!level) return;
      if (number) number.textContent = level.dataset.number;
      if (heading) heading.textContent = level.dataset.heading;
      if (text) text.textContent = level.dataset.text;
      input.setAttribute('aria-valuetext', `${level.dataset.number}: ${level.dataset.heading}`);
      markers.forEach((marker) => {
        marker.classList.toggle('is-active', marker.dataset.roseProcessingMarker === level.dataset.levelIndex);
      });
      productPanels.forEach((panel) => {
        panel.hidden = panel.dataset.levelIndex !== level.dataset.levelIndex;
      });
    };

    input.addEventListener('input', updateExplorer);
    updateExplorer();
  };

  const prepareProcessingFilter = (nav) => {
    if (preparedProcessingFilters.has(nav)) return;
    preparedProcessingFilters.add(nav);

    const links = Array.from(nav.querySelectorAll('[data-rose-processing-value]'));
    const filterParam = nav.dataset.roseProcessingParam;
    const grid = document.getElementById('ProductGridContainer');
    const summary = nav.querySelector('[data-rose-processing-summary]');
    const allLabel = nav.dataset.roseProcessingAllLabel || 'All levels';
    const selectedLabel = nav.dataset.roseProcessingSelectedLabel || 'levels selected';

    if (!links.length || !filterParam || !grid) return;

    const sync = () => {
      const activeValues = new URL(window.location.href).searchParams.getAll(filterParam);
      links.forEach((link) => {
        const value = link.dataset.roseProcessingValue;
        const active = value ? activeValues.includes(value) : activeValues.length === 0;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (summary) {
        const selectedLinks = links.filter((link) => {
          const value = link.dataset.roseProcessingValue;
          return value && activeValues.includes(value);
        });

        if (selectedLinks.length === 0) {
          summary.textContent = allLabel;
        } else if (selectedLinks.length === 1) {
          summary.textContent = selectedLinks[0].dataset.roseProcessingLabel || selectedLinks[0].textContent.trim();
        } else {
          summary.textContent = `${selectedLinks.length} ${selectedLabel}`;
        }
      }
    };

    const observer = new MutationObserver(() => {
      nav.removeAttribute('aria-busy');
      sync();
    });
    observer.observe(grid, { childList: true });

    nav.addEventListener('click', (event) => {
      const link = event.target.closest('[data-rose-processing-value]');
      if (!link || !nav.contains(link) || typeof FacetFiltersForm === 'undefined') return;

      event.preventDefault();
      const url = new URL(window.location.href);
      const value = link.dataset.roseProcessingValue;
      const activeValues = url.searchParams.getAll(filterParam);
      const removeCurrentValue = value && activeValues.includes(value);

      url.searchParams.delete(filterParam);
      url.searchParams.delete('page');
      if (value) {
        activeValues
          .filter((activeValue) => activeValue !== value)
          .forEach((activeValue) => url.searchParams.append(filterParam, activeValue));
        if (!removeCurrentValue) url.searchParams.append(filterParam, value);
      }

      nav.setAttribute('aria-busy', 'true');
      FacetFiltersForm.renderPage(url.searchParams.toString());
      sync();
    });

    window.addEventListener('popstate', sync);
    sync();
  };

  const prepareProductRail = (rail) => {
    if (preparedProductRails.has(rail)) return;
    preparedProductRails.add(rail);

    const section = rail.closest('.rose-featured-products');
    const previous = section?.querySelector('[data-rose-product-previous]');
    const next = section?.querySelector('[data-rose-product-next]');
    if (!previous || !next) return;

    const updateControls = () => {
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      previous.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= maxScroll - 2;
    };

    const move = (direction) => {
      const card = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const distance = (card?.getBoundingClientRect().width || rail.clientWidth) + gap;
      rail.scrollBy({ left: direction * distance, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    rail.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);
    updateControls();
  };

  const prepareRoastSchedule = (schedule) => {
    if (preparedRoastSchedules.has(schedule)) return;
    preparedRoastSchedules.add(schedule);

    const now = new Date();
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Zurich',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
      })
        .formatToParts(now)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
    const weekday = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[parts.weekday];
    if (weekday === undefined) return;

    const daysToMonday = weekday === 0 ? 1 : 8 - weekday;
    const zurichToday = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12));
    const roastDate = new Date(zurichToday);
    roastDate.setUTCDate(roastDate.getUTCDate() + daysToMonday);
    const cutoffDate = new Date(roastDate);
    cutoffDate.setUTCDate(cutoffDate.getUTCDate() - 1);

    const locale = document.documentElement.lang || 'en-CH';
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: 'Europe/Zurich',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const cutoff = schedule.querySelector('[data-rose-roast-cutoff]');
    const roast = schedule.querySelector('[data-rose-roast-date]');
    if (cutoff) cutoff.textContent = `${formatter.format(cutoffDate)} at 23:59`;
    if (roast) roast.textContent = formatter.format(roastDate);
  };

  const prepareRangeStory = (story) => {
    if (preparedRangeStories.has(story)) return;
    preparedRangeStories.add(story);

    const stage = story.querySelector('[data-rose-range-stage]');
    const steps = Array.from(story.querySelectorAll('[data-rose-range-step]'));
    const media = Array.from(story.querySelectorAll('[data-rose-range-media]'));
    const anchors = Array.from(story.querySelectorAll('[data-rose-range-media-anchor]'));
    const current = story.querySelector('[data-rose-range-current]');
    const desktop = window.matchMedia('(min-width: 990px)');
    let enhanced = false;
    let frame;

    if (!stage || !steps.length || steps.length !== media.length || media.length !== anchors.length) return;

    const setActive = (index) => {
      const activeIndex = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === activeIndex));
      media.forEach((visual, mediaIndex) => visual.classList.toggle('is-active', mediaIndex === activeIndex));
      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
    };

    const update = () => {
      frame = undefined;
      if (!enhanced) return;

      const viewportTarget = window.innerHeight * 0.5;
      const storyBounds = story.getBoundingClientRect();
      let activeIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      steps.forEach((step, index) => {
        const bounds = step.getBoundingClientRect();
        const distance = Math.abs(bounds.top + bounds.height * 0.5 - viewportTarget);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });

      const stickyOffset = 84;
      const scrollDistance = Math.max(1, story.offsetHeight - window.innerHeight + stickyOffset);
      const progress = Math.max(0, Math.min(1, (stickyOffset - storyBounds.top) / scrollDistance));
      story.style.setProperty('--rose-range-story-progress', progress.toFixed(4));
      setActive(activeIndex);
    };

    const requestUpdate = () => {
      if (frame !== undefined) return;
      frame = window.requestAnimationFrame(update);
    };

    const enable = () => {
      if (enhanced || !desktop.matches || reducedMotion.matches) return;
      enhanced = true;
      story.classList.add('is-enhanced');
      media.forEach((visual) => stage.append(visual));
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      update();
    };

    const disable = () => {
      if (!enhanced) return;
      enhanced = false;
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = undefined;
      story.classList.remove('is-enhanced');
      story.style.removeProperty('--rose-range-story-progress');
      media.forEach((visual, index) => anchors[index].after(visual));
      setActive(0);
    };

    const sync = () => {
      if (desktop.matches && !reducedMotion.matches) {
        enable();
      } else {
        disable();
      }
    };

    const handleBlockSelect = (event) => {
      const step = event.target.closest('[data-rose-range-step]');
      if (!enhanced || !step || !story.contains(step)) return;
      setActive(Number(step.dataset.roseRangeIndex));
      step.scrollIntoView({ behavior: 'auto', block: 'center' });
    };

    desktop.addEventListener('change', sync);
    reducedMotion.addEventListener('change', sync);
    document.addEventListener('shopify:block:select', handleBlockSelect);
    rangeStoryCleanups.set(story, () => {
      disable();
      desktop.removeEventListener('change', sync);
      reducedMotion.removeEventListener('change', sync);
      document.removeEventListener('shopify:block:select', handleBlockSelect);
      preparedRangeStories.delete(story);
      rangeStoryCleanups.delete(story);
    });
    sync();
  };

  const prepare = (root = document) => {
    const elements = root.querySelectorAll('[data-rose-reveal]');

    elements.forEach((element) => {
      if (observed.has(element)) return;
      observed.add(element);

      if (reducedMotion.matches || !observer) {
        reveal(element);
        return;
      }

      observer.observe(element);
    });

    root.querySelectorAll('[data-rose-autoplay-video]').forEach((video) => {
      if (preparedVideos.has(video)) return;
      preparedVideos.add(video);

      if (reducedMotion.matches) {
        video.pause();
        return;
      }

      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    });

    root.querySelectorAll('[data-rose-finder]').forEach(prepareFinder);
    root.querySelectorAll('[data-rose-header]').forEach(prepareHeader);
    root.querySelectorAll('[data-rose-processing-explorer]').forEach(prepareProcessingExplorer);
    root.querySelectorAll('[data-rose-processing-filter]').forEach(prepareProcessingFilter);
    root.querySelectorAll('[data-rose-product-scroll]').forEach(prepareProductRail);
    root.querySelectorAll('[data-rose-roast-schedule]').forEach(prepareRoastSchedule);
    root.querySelectorAll('[data-rose-range-story]').forEach(prepareRangeStory);
  };

  const start = () => {
    if (!reducedMotion.matches && 'IntersectionObserver' in window) {
      document.documentElement.classList.add('rose-motion-ready');
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    prepare();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-rose-range-story]').forEach((story) => rangeStoryCleanups.get(story)?.());
  });

  reducedMotion.addEventListener('change', () => {
    document.querySelectorAll('[data-rose-autoplay-video]').forEach((video) => {
      if (reducedMotion.matches) {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }
    });
  });
})();
